import React, { useState, useCallback } from "react";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import "./RatingButton.css";
import navStyles from "./RatingButton.nav.module.scss";
import FeatureRequestModal from "pages/HelpDesk/FeatureRequestModal";
import GetFeedbackModal from "pages/HelpDesk/GetFeedbackModal";
import ReportIssuesModal from "pages/HelpDesk/ReportIssuesModal";
import WhatsNewModal from "pages/HelpDesk/WhatsNewModal";
import DisplayDetailsModal from "pages/HelpDesk/DisplayDetailsModal";
import { withUserContext, withApiContext } from "context";
import { IconGeneralfdbk, IconSuggestion, IconContentConcern } from "Icons";
import { IconHelp } from "re_redesigned_componnents/icon";
import { useTheme } from "styled-components";

const helpOptions = [
  {
    icon: <IconGeneralfdbk />,
    text: "General Feedback",
    modal: "GetFeedbackModal",
  },
  {
    icon: <IconContentConcern />,
    text: "Content Concern",
    modal: "ReportIssuesModal",
  },
  {
    icon: <IconSuggestion />,
    text: "Suggestions",
    modal: "FeatureRequestModal",
  },
  {
    icon: <NotificationsNoneIcon />,
    text: "What's New",
    modal: "WhatsNewModal",
  },
];

const RatingButton = ({ ctx_api, variant = "fab" }) => {
  const isNav = variant === "nav";
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [formData, setFormData] = useState({});
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const theme = useTheme();

  const handleMouseEnter = useCallback(() => {
    if (isNav) return;
    setIsVisible(true);
  }, [isNav]);

  const handleMouseLeave = useCallback(() => {
    if (isNav) return;
    if (!isExpanded) {
      setIsVisible(false);
    }
  }, [isNav, isExpanded]);

  const handleDivClick = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleNavTriggerClick = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleOptionClick = useCallback(
    (option, event) => {
      if (option.modal === "WhatsNewModal") {
        setAnchorEl(event.currentTarget);
      }

      setActiveModal(option.modal);
      setSelectedOption(option.text);
      if (isNav && option.modal !== "WhatsNewModal") {
        setIsExpanded(false);
      }
    },
    [isNav]
  );

  const handleModalClose = useCallback(() => {
    setActiveModal(null);
    setAnchorEl(null);
    setSelectedOption(null);
    if (isNav) {
      setIsExpanded(false);
    }
  }, [isNav]);

  const handleBlurBackgroundClick = useCallback(() => {
    setIsExpanded(false);
    setIsVisible(false);
    setActiveModal(null);
    setAnchorEl(null);
    setSelectedOption(null);
  }, []);

  const handleFormDataUpdate = async (data) => {
    try {
      console.log("Incoming Data:", data);

      const optionalIntegerFields = ["course_id", "test_assessment_id"];
      const filteredData = Object.fromEntries(
        Object.entries(data)
          .map(([key, value]) => {
            if (value === undefined) return null;

            if (value === null && optionalIntegerFields.includes(key)) {
              return [key, null];
            }
            return [key, value];
          })
          .filter(Boolean)
      );

      console.log("Filtered Data before API request:", filteredData);
      Object.keys(filteredData).forEach((key) => {
        console.log(
          `${key}: ${filteredData[key]} (type: ${typeof filteredData[key]})`
        );
      });

      if (Object.keys(filteredData).length === 0) {
        console.warn("Warning: Filtered data is empty! Check input values.");
        return;
      }

      setFormData((prevData) => ({ ...prevData, ...filteredData }));

      const response = await ctx_api.privateRequest({
        cmd: "insert_update_help_desk_data",
        args: filteredData,
      });

      console.log("Help desk data submitted successfully:", response);
      if (response.rsp.status === 1) {
        const responseData = {
          ticketId: response.rsp.ticket_id,
          createdOn: response.rsp.created_on,
          description: response.rsp.description,
          ticketType: response.rsp.ticket_type,
          message: response.rsp.message,
        };
        setFormData(responseData);
        handleModalClose();
        setIsDetailsModalOpen(true);
      } else {
        throw new Error(response.rsp.message || "Submission failed");
      }
    } catch (error) {
      console.error("Error submitting help desk data:", error);
    }
  };

  const renderHelpIconFab = () => {
    if (!isVisible) return <ContactSupportIcon className="help-icon active" />;
    return isExpanded ? (
      <ExpandMoreIcon className="help-icon active" />
    ) : (
      <ExpandLessIcon className="help-icon active" />
    );
  };

  const renderActiveModal = () => {
    switch (activeModal) {
      case "GetFeedbackModal":
        return (
          <GetFeedbackModal
            isOpen={true}
            onClose={handleModalClose}
            onSubmit={handleFormDataUpdate}
          />
        );
      case "ReportIssuesModal":
        return (
          <ReportIssuesModal
            isOpen={true}
            onClose={handleModalClose}
            onSubmit={handleFormDataUpdate}
          />
        );
      case "FeatureRequestModal":
        return (
          <FeatureRequestModal
            isOpen={true}
            onClose={handleModalClose}
            onSubmit={handleFormDataUpdate}
          />
        );
      case "WhatsNewModal":
        return (
          <WhatsNewModal
            isOpen={true}
            onClose={handleModalClose}
            anchorEl={anchorEl}
            ctx_api={ctx_api}
          />
        );
      default:
        return null;
    }
  };

  const getOptionClassName = (option) => {
    const baseClass = "help-option";
    if (!selectedOption) return baseClass;
    if (selectedOption === option.text) {
      return `${baseClass} help-option-selected`;
    }
    return `${baseClass} help-option-muted`;
  };

  const renderOptionsList = (containerClassName) => (
    <div className={containerClassName}>
      {helpOptions.map((option, index) => {
        const isWhatsNew = option.text === "What's New";
        const iconColor = theme.color2;
        const coloredIcon = isWhatsNew
          ? option.icon
          : React.cloneElement(option.icon, {
              style: { color: iconColor },
            });

        return (
          <div
            key={index}
            className={getOptionClassName(option)}
            onClick={(event) => handleOptionClick(option, event)}
            aria-selected={selectedOption === option.text}
          >
            <div
              className={`help-option-content ${
                option.text === "What's New"
                  ? "help-option-content-animated"
                  : ""
              }`}
            >
              {coloredIcon}
              <span
                className={`help-option-text ${
                  option.text === "What's New" ? "help-option-text-animated" : ""
                }`}
                style={{ color: isWhatsNew ? undefined : iconColor }}
              >
                {option.text}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );

  const sharedChrome = (
    <>
      <div
        className={`blur-background ${
          isExpanded || activeModal ? "active" : ""
        }`}
        onClick={handleBlurBackgroundClick}
      />
      {renderActiveModal()}
      {isDetailsModalOpen && (
        <DisplayDetailsModal
          formData={formData}
          isOpen={isDetailsModalOpen}
          onClose={() => setIsDetailsModalOpen(false)}
        />
      )}
    </>
  );

  if (isNav) {
    return (
      <>
        {sharedChrome}
        <div
          className={`${navStyles.navRoot} ${
            isExpanded || activeModal ? navStyles.navRootOpen : ""
          }`}
        >
          <button
            type="button"
            className={navStyles.navTrigger}
            onClick={handleNavTriggerClick}
            aria-expanded={isExpanded}
            aria-haspopup="true"
            aria-label="Help and feedback"
            title="Help and feedback"
          >
            {isExpanded ? (
              <ExpandMoreIcon className={navStyles.navChevron} />
            ) : (
              <IconHelp aria-hidden />
            )}
          </button>
          {isExpanded &&
            renderOptionsList(
              `${navStyles.navDropdown} help-options-container help-options-container--nav`
            )}
        </div>
      </>
    );
  }

  return (
    <>
      {sharedChrome}
      <div className="fixed-help-container">
        <div
          className="help-wrapper"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div
            className={`help-content ${isVisible ? "expanded" : "collapsed"}`}
            style={{ backgroundColor: theme.color2 }}
            onClick={handleDivClick}
          >
            <div className={`help-text ${isVisible ? "visible" : "hidden"}`}>
              <span>Need Help?</span>
            </div>
            {renderHelpIconFab()}
          </div>
          {isExpanded && renderOptionsList("help-options-container")}
        </div>
      </div>
    </>
  );
};

export default withUserContext(withApiContext(React.memo(RatingButton)));
